/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import {
  addDays,
  fromYMD,
  getYear,
  isLess,
  isLessOrEqual,
  maxDayDate
} from '../day-date';

/**
 * @private
 * @typedef {import('../day-date').DayDate} DayDate
 * @typedef {import('./day-interval').default} DayInterval
 */

/**
 * Transformační generátor denních intervalů, tj. funkce akceptující iterátor
 * a vracející generátor.
 * @typedef {import('../generator/generator-util').GeneratorTransformer<DayInterval>} DayIntervalGeneratorTransformer
 */

/**
 * Generátor roustoucích a nepřekrývajících se intervalů, který zpracovává
 * neklesající a případně se překrývající vstupní intervaly.
 * @param {IterableIterator<DayInterval>} iterator Iterátor intervalů s neklesajícími začátky.
 * @returns {Generator<DayInterval>} Generátor rostoucích a nepřekrývajících se intervalů.
 */
export function* nonOverlapping(iterator) {
  /** @type {DayInterval} */
  let last;
  let current = iterator.next();
  while (!current.done) {
    yield current.value;
    last = current.value;

    // Přeskočíme všechny následující intervaly, které jsou pokryté posledním
    // intervalem.
    do {
      current = iterator.next();
      // Pokud je konec iterátoru, tak se vrátíme, protože yield již proběhnul.
      if (current.done) {
        return;
      }
    } while (isLessOrEqual(current.value.end, last.end));

    // Pokud se last a current intervaly překrývají, tak odstraníme překrytí
    // a začátek smyčky yieldne upravený current.
    if (isLessOrEqual(current.value.start, last.end)) {
      // TODO: Otázka, zda tato operace může vrhnout.
      // Při dodržení vstupních invariantů zřejmě nikoliv.
      // Ale chtělo by to dokázat.
      current.value = current.value.setStart(addDays(last.end, 1));
    }
  }
}

/**
 * Tranformační generátor, který pro danou neklesající a nepřekrývající sekvenci
 * intervalů přeskáče na aktuálně probíhající nebo první následující interval
 * vzhledem k {@link today}.
 * @param {DayDate} today
 * @returns {DayIntervalGeneratorTransformer}
 */
export function skipToCurrentOrNext(today) {
  return function* (iterator) {
    for (const interval of iterator) {
      if (isLessOrEqual(today, interval.end)) {
        yield interval;
        yield* iterator;
        break;
      }
    }
  };
}

/**
 * Tranformační generátor, který pro danou neklesající a nepřekrývající sekvenci
 * intervalů přeskáče na aktuální rok vzhledem k {@link today}.
 * @param {DayDate} today
 * @returns {DayIntervalGeneratorTransformer}
 */
export function skipToCurrentYear(today) {
  const currentYear = getYear(today);
  return function* (iterator) {
    for (const interval of iterator) {
      if (getYear(interval.start) >= currentYear) {
        yield interval;
        yield* iterator;
        break;
      }
    }
  };
}

/**
 * Transformační generátor roustoucích a nepřekrývající se intervalů případně
 * dělící roustoucí a nepřekrývající se intervaly delší jak {@link days} dní
 * na intervaly o délce {@link days} dní, kde poslední zbytek může být kratší.
 * @param {number} days Celé číslo v rozsahu [1, Number.MAX_SAFE_INTEGER].
 * @returns {DayIntervalGeneratorTransformer}
 * @throws {RangeError} Před vrácením generátoru, pokud {@link days} není
 * v daném rozsahu. Nebo uvnitř generátoru, pokud dojde k přetečení rozsahu
 * {@link DayDate}.
 */
export function splitByDays(days) {
  if (!(Number.isSafeInteger(days) && days > 0)) {
    throw new RangeError(
      `days=${days}, but 0 < days <= Number.MAX_SAFE_INTEGER`
    );
  }
  return function* (iterator) {
    let current = iterator.next();
    while (!current.done) {
      // Výpočet nextStart může vrhnout, ale kvůli záruce kladného days
      // to může jen znamenat, že nextStart je za rozsahem DayDate,
      // proto saturujeme na maximální datum.
      let nextStart = maxDayDate;
      try {
        nextStart = addDays(current.value.start, days);
      } catch {}
      if (isLess(current.value.end, nextStart)) {
        yield current.value;
        current = iterator.next();
      } else {
        yield current.value.setEnd(addDays(nextStart, -1));
        current.value = current.value.setStart(nextStart);
      }
    }
  };
}

/**
 * Generátor roustoucích a nepřekrývajících se intervalů případně dělící
 * roustoucí a nepřekrývající se intervaly přes hranici roku tak, aby každý
 * výstupní interval spadal právě do jednoho roku.
 * @param {IterableIterator<DayInterval>} iterator
 * @returns {Generator<DayInterval>}
 */
export function* splitOnYear(iterator) {
  let next = iterator.next();
  while (!next.done) {
    if (getYear(next.value.start) === getYear(next.value.end)) {
      // Pokud je start a end ve stejném roce, tak jen vrátíme interval
      // bez modifikací a načteme další.
      yield next.value;
      next = iterator.next();
    } else {
      // Pokud jsou roky start a end jiné, tak rozdělíme interval
      // do konce start roku a odešleme a do next uložíme zbytek
      // od počátku následujícího roku. Zbytek může být delší i než rok,
      // ale to se případně iterativně vyřeší ve smyčce.
      const endOfYear = fromYMD(getYear(next.value.start), 12, 31);
      yield next.value.setEnd(endOfYear);
      next.value = next.value.setStart(addDays(endOfYear, 1));
    }
  }
}
